import { DateTime, Duration } from "luxon";

import type { WsMembershipAvailability } from "@storyboard/web-client";
import { bootstrapWorkspaceMembershipAvailabilities } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { bulkUpsertWsMembershipAvailability } from "@/models/repositories/WsMembershipAvailability";
import { flightRecorder } from "@/services/debug/FlightRecorder";

const log = createLogger("bootstrap");

const AVAILABILITY_INCREMENTAL_CURSOR = "availabilityIncrementalCursor";
const AVAILABILITY_FULL_SWEEP = "availabilityFullSweep";

export const AVAILABILITY_PAGE_SIZE = 1000;
/**
 * Circuit breaker: 50 pages × 1000/page = 50k memberships covers any plausible workspace and
 * stops a runaway loop if the server ever returns full pages indefinitely.
 */
export const AVAILABILITY_MAX_PAGES = 50;
/** How often a poll ignores the cursor and re-walks every row, reconciling any drift. */
export const AVAILABILITY_FULL_SWEEP_INTERVAL_MS = Duration.fromObject({ hours: 24 }).toMillis();
/**
 * Wound back from the sync start time when advancing the cursor, so a client clock running ahead
 * of the server can't skip rows written inside the skew window. The overlap re-upserts
 * idempotently.
 */
export const AVAILABILITY_CURSOR_SKEW_BUFFER_MS = Duration.fromObject({ minutes: 1 }).toMillis();

/**
 * Read/write side of the persistent availability cursor. Production code passes through to
 * {@link BootstrapEventsRegistry} (IndexedDB-backed); tests inject an in-memory stub.
 */
export type AvailabilityCursorStore = {
  getEventDate: (event: string, workspaceId: string) => Promise<Date | undefined>;
  setEventDate: (event: string, workspaceId: string, date: Date) => Promise<void>;
};

export type AvailabilitySyncDeps = {
  store: AvailabilityCursorStore;
  /** Fetches one page; `timestamp` is undefined on full walks (no cursor, or a sweep is due). */
  fetchPage: (args: {
    workspaceId: string;
    page: number;
    pageSize: number;
    timestamp?: string;
  }) => Promise<readonly WsMembershipAvailability[]>;
  /** Merges fetched rows into the local DB via the existing bulk-upsert path. */
  apply: (records: readonly WsMembershipAvailability[]) => Promise<void>;
  now: () => Date;
};

const registryStore: AvailabilityCursorStore = {
  getEventDate: (event, workspaceId) => BootstrapEventsRegistry.getInstance().getLatestEventDate(event, workspaceId),
  setEventDate: async (event, workspaceId, date) => {
    await BootstrapEventsRegistry.getInstance().markBootstrap(event, undefined, workspaceId, date);
  },
};

const productionDeps: AvailabilitySyncDeps = {
  store: registryStore,
  fetchPage: async ({ workspaceId, page, pageSize, timestamp }) => {
    const response = await bootstrapWorkspaceMembershipAvailabilities(transport, {
      workspaceId,
      page,
      pageSize,
      timestamp,
    });
    return response?.membershipAvailabilities ?? [];
  },
  apply: (records) => bulkUpsertWsMembershipAvailability([...records]),
  now: () => new Date(),
};

/**
 * Refreshes the local availability table for `workspaceId`, incrementally when possible.
 *
 * Full walks happen when no cursor exists (first boot, post-wipe) and every 24 h thereafter —
 * workspaces like Trimac have thousands of memberships and anyone the walk misses is stuck on
 * stale availability, rendering as offline forever, so the periodic sweep reconciles any drift
 * (partial upsert failures, rows lost without a registry wipe). Every other sync passes the
 * stored cursor as the endpoint's `timestamp` filter, so the 5-minute re-poll downloads only rows
 * that changed since the previous poll instead of up to 50k rows per tick (WEB-2202).
 * Availability rows update in place (no hard deletes), so a cursor miss cannot orphan tombstones.
 *
 * The next cursor is the sync's *start* time minus a skew buffer, so rows written server-side
 * mid-walk — or timestamped behind a fast client clock — are re-fetched on the next poll rather
 * than skipped. A walk truncated by the page circuit breaker advances nothing, so unfetched rows
 * stay inside the next poll's window.
 */
export async function syncWorkspaceMembershipAvailabilities(
  workspaceId: string,
  deps: AvailabilitySyncDeps = productionDeps,
): Promise<void> {
  const requestStart = deps.now();

  const lastFullSweep = await deps.store.getEventDate(AVAILABILITY_FULL_SWEEP, workspaceId);
  const fullSweepDue =
    !lastFullSweep ||
    DateTime.fromJSDate(requestStart).diff(DateTime.fromJSDate(lastFullSweep)).toMillis() >=
      AVAILABILITY_FULL_SWEEP_INTERVAL_MS;
  const cursorDate = fullSweepDue
    ? undefined
    : await deps.store.getEventDate(AVAILABILITY_INCREMENTAL_CURSOR, workspaceId);
  const timestamp = cursorDate?.toISOString();

  for (let page = 0; ; page++) {
    const records = await deps.fetchPage({ workspaceId, page, pageSize: AVAILABILITY_PAGE_SIZE, timestamp });

    if (records.length > 0) {
      flightRecorder.note("sync:availability");
      await deps.apply(records);
    }

    if (records.length < AVAILABILITY_PAGE_SIZE) break;
    if (page + 1 >= AVAILABILITY_MAX_PAGES) {
      log.warn("[syncWorkspaceMembershipAvailabilities] Hit max page limit; stopping pagination", {
        workspaceId,
        maxPages: AVAILABILITY_MAX_PAGES,
        pageSize: AVAILABILITY_PAGE_SIZE,
      });
      // Rows past the limit were never fetched; advancing the cursor would filter them out of
      // every future poll, permanently. Leave both markers so the next poll retries the window.
      return;
    }
  }

  const nextCursor = DateTime.fromJSDate(requestStart)
    .minus({ milliseconds: AVAILABILITY_CURSOR_SKEW_BUFFER_MS })
    .toJSDate();
  await deps.store.setEventDate(AVAILABILITY_INCREMENTAL_CURSOR, workspaceId, nextCursor);
  if (fullSweepDue) {
    await deps.store.setEventDate(AVAILABILITY_FULL_SWEEP, workspaceId, requestStart);
  }
}
